/** CGRates Status page for monitoring billing system health. */
import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
} from "@mui/material";
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Phone as PhoneIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { cgratesApi } from "../api/client";

/** CGRates status interface. */
interface CGRatesStatus {
  healthy: boolean;
  version?: string;
  active_sessions: number;
  components: {
    sessions: boolean;
    rater: boolean;
    lcr: boolean;
    cdrs: boolean;
  };
  error?: string;
}

export default function CGRatesStatusPage() {
  const [status, setStatus] = useState<CGRatesStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  const fetchStatus = async () => {
    setRefreshing(true);
    try {
      const response = await cgratesApi.getStatus();
      setStatus(response.data);
    } catch (err) {
      setStatus({
        healthy: false,
        active_sessions: 0,
        components: {
          sessions: false,
          rater: false,
          lcr: false,
          cdrs: false,
        },
        error: "Failed to connect to the billing engine",
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLastUpdate(new Date());
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Billing & Rating Status</Typography>
          <IconButton onClick={fetchStatus} disabled={refreshing} title="Refresh" aria-label="Refresh">
            <RefreshIcon className={refreshing ? "rotating" : ""} />
          </IconButton>
        </Box>

        {!status?.healthy && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {status?.error || "Billing & Rating engine is unavailable"}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* System Status Card */}
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  {status?.healthy ? (
                    <CheckIcon color="success" />
                  ) : (
                    <ErrorIcon color="error" />
                  )}
                  <Typography variant="body2" color="text.secondary">
                    System Status
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ mt: 1 }}>
                  {status?.healthy ? "Healthy" : "Unhealthy"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Active Sessions Card */}
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <PhoneIcon color="action" />
                  <Typography variant="body2" color="text.secondary">
                    Active Sessions
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ mt: 1 }}>
                  {status?.active_sessions ?? 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Version Card */}
          {status?.version && (
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Version
                  </Typography>
                  <Typography variant="h5" sx={{ mt: 1 }}>
                    {status.version}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Components Status Card */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Components
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip
                label="SessionS"
                color={status?.components.sessions ? "success" : "error"}
                size="medium"
              />
              <Chip
                label="RaterS"
                color={status?.components.rater ? "success" : "error"}
                size="medium"
              />
              <Chip
                label="LCRS"
                color={status?.components.lcr ? "success" : "error"}
                size="medium"
              />
              <Chip
                label="CDRs"
                color={status?.components.cdrs ? "success" : "error"}
                size="medium"
              />
            </Box>
          </Grid>
        </Grid>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: "block" }}>
          Last updated: {lastUpdate.toLocaleTimeString()} (Auto-refresh every 30s)
        </Typography>
      </Paper>

      <style>{`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .rotating {
          animation: rotate 1s linear infinite;
        }
      `}</style>
    </Container>
  );
}
